import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Target, Plus, Trash2, Edit, TrendingUp, DollarSign,
  Wallet, Shield, PiggyBank, CheckCircle, Clock,
  Calendar, Loader2, Award, Flame, History, Star, Zap
} from "lucide-react";

const CATEGORIES = [
  { value: "savings", label: "Savings", icon: PiggyBank, color: "emerald" },
  { value: "debt_payoff", label: "Debt Payoff", icon: Wallet, color: "red" },
  { value: "investment", label: "Investment Growth", icon: TrendingUp, color: "blue" },
  { value: "emergency_fund", label: "Emergency Fund", icon: Shield, color: "amber" },
  { value: "retirement", label: "Retirement", icon: Award, color: "violet" },
  { value: "custom", label: "Custom Goal", icon: Target, color: "cyan" },
];

const getCatMeta = (cat) => CATEGORIES.find(c => c.value === cat) || CATEGORIES[5];

const formatCurrency = (v) => {
  const n = parseFloat(v) || 0;
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

export default function Goals() {
  const { api } = useAuth();
  const [goals, setGoals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [celebration, setCelebration] = useState(null);
  const [historyGoal, setHistoryGoal] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "", category: "savings", target_amount: "", current_amount: "",
    deadline: "", monthly_contribution: "", notes: ""
  });

  const loadGoals = useCallback(async () => {
    try {
      const [goalsRes, summaryRes] = await Promise.all([
        api.get("/goals"), api.get("/goals/summary")
      ]);
      setGoals(goalsRes.data);
      setSummary(summaryRes.data);
    } catch { toast.error("Failed to load goals"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { loadGoals(); }, [loadGoals]);

  const resetForm = () => {
    setFormData({ name: "", category: "savings", target_amount: "", current_amount: "", deadline: "", monthly_contribution: "", notes: "" });
    setEditing(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.target_amount) { toast.error("Name and target amount required"); return; }
    try {
      const payload = {
        ...formData,
        target_amount: parseFloat(formData.target_amount) || 0,
        current_amount: parseFloat(formData.current_amount) || 0,
        monthly_contribution: parseFloat(formData.monthly_contribution) || 0,
      };
      if (editing) {
        await api.put(`/goals/${editing}`, payload);
        toast.success("Goal updated");
      } else {
        await api.post("/goals", payload);
        toast.success("Goal created");
      }
      resetForm();
      loadGoals();
    } catch { toast.error("Failed to save goal"); }
  };

  const startEdit = (goal) => {
    setFormData({
      name: goal.name, category: goal.category,
      target_amount: goal.target_amount.toString(),
      current_amount: goal.current_amount.toString(),
      deadline: goal.deadline || "",
      monthly_contribution: goal.monthly_contribution.toString(),
      notes: goal.notes || "",
    });
    setEditing(goal.id);
    setShowForm(true);
  };

  const deleteGoal = async (id) => {
    if (!window.confirm("Delete this goal?")) return;
    try { await api.delete(`/goals/${id}`); toast.success("Goal deleted"); loadGoals(); }
    catch { toast.error("Failed to delete"); }
  };

  const updateProgress = async (goal, amount) => {
    try {
      const res = await api.post(`/goals/${goal.id}/contribute`, { amount, note: `Quick add +$${amount}` });
      const data = res.data;
      if (data.milestones_hit && data.milestones_hit.length > 0) {
        setCelebration({ goalName: goal.name, milestones: data.milestones_hit, justCompleted: data.just_completed });
        setTimeout(() => setCelebration(null), 4000);
      }
      if (data.just_completed) {
        toast.success(`${goal.name} completed!`);
      }
      loadGoals();
    } catch { toast.error("Failed to update"); }
  };

  const showHistory = async (goal) => {
    setHistoryGoal(goal);
    try {
      const res = await api.get(`/goals/${goal.id}/contributions`);
      setContributions(res.data);
    } catch { setContributions([]); }
  };

  const loadInsights = async () => {
    setInsightsLoading(true);
    try {
      const res = await api.post("/goals/ai-insights");
      setInsights(res.data);
    } catch { toast.error("Failed to generate insights"); }
    finally { setInsightsLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="goals-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Financial Goals</h1>
          <p className="text-sm text-zinc-500 mt-1">Track savings, debt payoff & investment milestones</p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-emerald-500 text-black hover:bg-emerald-600 rounded-sm" data-testid="add-goal-btn">
          <Plus className="w-4 h-4 mr-2" /> New Goal
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && summary.total_goals > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Goals</p>
              <p className="text-2xl font-bold text-white font-mono mt-1" data-testid="total-goals">{summary.total_goals}</p>
              <p className="text-xs text-zinc-500 mt-1">{summary.completed} completed, {summary.in_progress} active</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Overall Progress</p>
              <p className="text-2xl font-bold text-emerald-400 font-mono mt-1" data-testid="overall-progress">{summary.overall_progress}%</p>
              <div className="h-1.5 bg-white/5 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-emerald-500 rounded-full transition-all duration-500" style={{ width: `${summary.overall_progress}%` }} />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Saved</p>
              <p className="text-2xl font-bold text-cyan-400 font-mono mt-1">{formatCurrency(summary.total_saved)}</p>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider">Target Total</p>
              <p className="text-2xl font-bold text-white font-mono mt-1">{formatCurrency(summary.total_target)}</p>
              <p className="text-xs text-zinc-500 mt-1">{formatCurrency(summary.total_target - summary.total_saved)} remaining</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AI Insights Section */}
      {goals.length > 0 && (
        <Card className="bg-[#0A0A0A] border-white/10 rounded-none">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-white flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-400" /> AI Financial Insights
              </CardTitle>
              <Button
                onClick={loadInsights}
                disabled={insightsLoading}
                variant="outline"
                size="sm"
                className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 rounded-sm text-xs"
                data-testid="ai-insights-btn"
              >
                {insightsLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />}
                {insightsLoading ? "Analyzing..." : insights ? "Refresh" : "Analyze My Goals"}
              </Button>
            </div>
          </CardHeader>
          {insights && (
            <CardContent className="pt-0 space-y-4">
              {/* Health Badge + Summary */}
              <div className={`p-3 rounded-sm border ${
                insights.overall_health === 'green' ? 'bg-emerald-500/10 border-emerald-500/20' :
                insights.overall_health === 'red' ? 'bg-red-500/10 border-red-500/20' :
                'bg-amber-500/10 border-amber-500/20'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-2 h-2 rounded-full ${
                    insights.overall_health === 'green' ? 'bg-emerald-500' :
                    insights.overall_health === 'red' ? 'bg-red-500' : 'bg-amber-500'
                  }`} />
                  <span className="text-xs uppercase tracking-wider text-zinc-400">
                    {insights.overall_health === 'green' ? 'On Track' : insights.overall_health === 'red' ? 'Needs Attention' : 'Review Recommended'}
                  </span>
                </div>
                <p className="text-sm text-zinc-300" data-testid="ai-overall-summary">{insights.overall_summary}</p>
              </div>

              {/* Alerts */}
              {insights.alerts && insights.alerts.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Goal Alerts</p>
                  {insights.alerts.map((alert, i) => (
                    <div key={i} className={`flex items-start gap-2 p-2 rounded-sm border ${
                      alert.type === 'behind_schedule' ? 'border-red-500/20 bg-red-500/5' :
                      alert.type === 'ahead' ? 'border-emerald-500/20 bg-emerald-500/5' :
                      alert.type === 'needs_attention' ? 'border-amber-500/20 bg-amber-500/5' :
                      'border-cyan-500/20 bg-cyan-500/5'
                    }`} data-testid={`alert-${i}`}>
                      {alert.type === 'behind_schedule' ? <Clock className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" /> :
                       alert.type === 'ahead' ? <CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /> :
                       alert.type === 'needs_attention' ? <Flame className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" /> :
                       <TrendingUp className="w-3.5 h-3.5 text-cyan-400 mt-0.5 shrink-0" />}
                      <div>
                        <span className="text-xs font-medium text-white">{alert.goal_name}</span>
                        <p className="text-xs text-zinc-400 mt-0.5">{alert.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Budget Allocation */}
              {insights.monthly_budget_suggestion && insights.monthly_budget_suggestion.recommended_allocation && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-500 uppercase tracking-wider">Recommended Monthly Allocation</p>
                    {insights.monthly_budget_suggestion.total_available > 0 && (
                      <span className="text-xs text-zinc-400 font-mono">{formatCurrency(insights.monthly_budget_suggestion.total_available)} available</span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {insights.monthly_budget_suggestion.recommended_allocation.map((alloc, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-white/5 rounded-sm border border-white/5">
                        <div>
                          <span className="text-xs text-white">{alloc.goal_name}</span>
                          <p className="text-[10px] text-zinc-500 mt-0.5">{alloc.reason}</p>
                        </div>
                        <span className="text-sm font-mono text-emerald-400 ml-2 shrink-0">{formatCurrency(alloc.suggested_monthly)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tips */}
              {insights.tips && insights.tips.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Tips</p>
                  {insights.tips.map((tip, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Star className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
                      <span className="text-xs text-zinc-400">{tip}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* Goals Grid */}
      {goals.length === 0 ? (
        <Card className="bg-[#0A0A0A] border-white/10 border-dashed rounded-none">
          <CardContent className="p-12 text-center">
            <Target className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400 text-lg">No financial goals yet</p>
            <p className="text-zinc-600 text-sm mt-1">Create your first goal to start tracking progress</p>
            <Button onClick={() => setShowForm(true)} className="mt-4 bg-emerald-500 text-black hover:bg-emerald-600 rounded-sm">
              <Plus className="w-4 h-4 mr-2" /> Create First Goal
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {goals.map((goal) => {
            const meta = getCatMeta(goal.category);
            const Icon = meta.icon;
            const colorMap = {
              emerald: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", bar: "bg-emerald-500" },
              red: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", bar: "bg-red-500" },
              blue: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400", bar: "bg-blue-500" },
              amber: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", bar: "bg-amber-500" },
              violet: { bg: "bg-violet-500/10", border: "border-violet-500/20", text: "text-violet-400", bar: "bg-violet-500" },
              cyan: { bg: "bg-cyan-500/10", border: "border-cyan-500/20", text: "text-cyan-400", bar: "bg-cyan-500" },
            };
            const c = colorMap[meta.color] || colorMap.cyan;
            const remaining = Math.max(goal.target_amount - goal.current_amount, 0);

            return (
              <Card key={goal.id} className={`bg-[#0A0A0A] ${goal.is_completed ? 'border-emerald-500/30' : 'border-white/10'} rounded-none relative overflow-hidden`} data-testid={`goal-card-${goal.id}`}>
                {goal.is_completed && (
                  <div className="absolute top-0 right-0 px-2 py-1 bg-emerald-500 text-black text-xs font-bold">
                    COMPLETED
                  </div>
                )}
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-sm ${c.bg}`}>
                        <Icon className={`w-5 h-5 ${c.text}`} />
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{goal.name}</h3>
                        <p className="text-xs text-zinc-500 capitalize">{meta.label}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(goal)} className="p-1.5 text-zinc-500 hover:text-white transition-colors" data-testid={`edit-goal-${goal.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteGoal(goal.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors" data-testid={`delete-goal-${goal.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Progress Bar with Milestone Markers */}
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-1.5">
                      <span className="text-sm font-mono text-white">{formatCurrency(goal.current_amount)}</span>
                      <span className="text-sm font-mono text-zinc-500">{formatCurrency(goal.target_amount)}</span>
                    </div>
                    <div className="relative">
                      <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${c.bar} rounded-full transition-all duration-700 ease-out`}
                          style={{ width: `${goal.progress_pct}%` }}
                        />
                      </div>
                      {/* Milestone markers */}
                      <div className="absolute top-0 left-0 w-full h-3 pointer-events-none">
                        {(goal.milestones || []).filter(m => m.pct < 100).map(m => (
                          <div
                            key={m.pct}
                            className="absolute top-0 h-full flex items-center"
                            style={{ left: `${m.pct}%`, transform: 'translateX(-50%)' }}
                          >
                            <div className={`w-1 h-full ${m.reached ? 'bg-white/40' : 'bg-white/10'}`} />
                          </div>
                        ))}
                      </div>
                      {/* Milestone labels below bar */}
                      <div className="flex justify-between mt-1 px-0">
                        {(goal.milestones || []).map(m => (
                          <div key={m.pct} className="flex flex-col items-center" style={{ width: '25%' }}>
                            {m.reached ? (
                              <Star className={`w-3 h-3 ${m.pct === 100 ? 'text-amber-400' : c.text}`} />
                            ) : (
                              <div className="w-3 h-3 rounded-full border border-white/10" />
                            )}
                            <span className={`text-[10px] mt-0.5 ${m.reached ? c.text : 'text-zinc-600'}`}>{m.pct}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className={`text-xs font-bold ${c.text}`}>{goal.progress_pct}%</span>
                      <span className="text-xs text-zinc-500">{formatCurrency(remaining)} to go</span>
                    </div>
                  </div>

                  {/* Quick Update + Streak */}
                  {!goal.is_completed && (
                    <div className="flex gap-2 mb-3">
                      {[100, 500, 1000].map((amt) => (
                        <button
                          key={amt}
                          onClick={() => updateProgress(goal, amt)}
                          className="flex-1 py-1.5 text-xs font-mono bg-white/5 hover:bg-white/10 text-zinc-300 rounded-sm border border-white/5 transition-colors"
                          data-testid={`quick-add-${amt}-${goal.id}`}
                        >
                          +${amt}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Meta Info */}
                  <div className="flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
                    {goal.streak > 0 && (
                      <div className="flex items-center gap-1 text-orange-400" data-testid={`streak-${goal.id}`}>
                        <Flame className="w-3 h-3" />
                        <span>{goal.streak} month streak</span>
                      </div>
                    )}
                    {goal.monthly_contribution > 0 && (
                      <div className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" />
                        <span>{formatCurrency(goal.monthly_contribution)}/mo</span>
                      </div>
                    )}
                    {goal.deadline && (
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>Due {new Date(goal.deadline).toLocaleDateString()}</span>
                      </div>
                    )}
                    {goal.projected_completion && !goal.is_completed && (
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>Est. {new Date(goal.projected_completion).toLocaleDateString()}</span>
                      </div>
                    )}
                    <button onClick={() => showHistory(goal)} className="flex items-center gap-1 hover:text-white transition-colors ml-auto" data-testid={`history-btn-${goal.id}`}>
                      <History className="w-3 h-3" /> History
                    </button>
                  </div>
                  {goal.notes && <p className="text-xs text-zinc-600 mt-2 italic">{goal.notes}</p>}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(v) => { if (!v) resetForm(); }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">{editing ? "Edit Goal" : "New Financial Goal"}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              {editing ? "Update your goal details" : "Set a new financial target to track"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Goal Name</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g. Emergency Fund" className="bg-black/50 border-white/10 rounded-sm" data-testid="goal-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Category</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="goal-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Target Amount</Label>
                <Input type="number" value={formData.target_amount} onChange={(e) => setFormData({...formData, target_amount: e.target.value})} placeholder="10000" className="bg-black/50 border-white/10 rounded-sm font-mono" data-testid="goal-target-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Current Amount</Label>
                <Input type="number" value={formData.current_amount} onChange={(e) => setFormData({...formData, current_amount: e.target.value})} placeholder="0" className="bg-black/50 border-white/10 rounded-sm font-mono" data-testid="goal-current-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Monthly Contribution</Label>
                <Input type="number" value={formData.monthly_contribution} onChange={(e) => setFormData({...formData, monthly_contribution: e.target.value})} placeholder="500" className="bg-black/50 border-white/10 rounded-sm font-mono" data-testid="goal-monthly-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Target Date</Label>
                <Input type="date" value={formData.deadline} onChange={(e) => setFormData({...formData, deadline: e.target.value})} className="bg-black/50 border-white/10 rounded-sm" data-testid="goal-deadline-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Notes (optional)</Label>
              <Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Any additional notes..." className="bg-black/50 border-white/10 rounded-sm" />
            </div>
            <Button type="submit" className="w-full bg-emerald-500 text-black hover:bg-emerald-600 rounded-sm" data-testid="save-goal-btn">
              {editing ? "Update Goal" : "Create Goal"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Celebration Overlay */}
      {celebration && (
        <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none" data-testid="celebration-overlay">
          <div className="relative">
            {/* Animated particles */}
            <div className="absolute inset-0 -m-32">
              {Array.from({ length: 20 }).map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    left: `${50 + (Math.random() - 0.5) * 80}%`,
                    top: `${50 + (Math.random() - 0.5) * 80}%`,
                    backgroundColor: ['#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ef4444'][i % 5],
                    animation: `celebration-particle ${1.5 + Math.random()}s ease-out forwards`,
                    animationDelay: `${Math.random() * 0.5}s`,
                    opacity: 0,
                  }}
                />
              ))}
            </div>
            <div className="bg-black/90 border border-emerald-500/30 rounded-sm p-8 text-center animate-bounce-in">
              {celebration.justCompleted ? (
                <>
                  <Award className="w-16 h-16 text-amber-400 mx-auto mb-3" />
                  <h2 className="text-2xl font-bold text-white">Goal Completed!</h2>
                  <p className="text-emerald-400 text-lg mt-1">{celebration.goalName}</p>
                </>
              ) : (
                <>
                  <Zap className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                  <h2 className="text-xl font-bold text-white">Milestone Reached!</h2>
                  <p className="text-emerald-400 mt-1">{celebration.goalName}</p>
                  <div className="flex justify-center gap-2 mt-3">
                    {celebration.milestones.map(m => (
                      <span key={m} className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-sm font-mono rounded-sm">
                        {m}%
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
          <style>{`
            @keyframes celebration-particle {
              0% { transform: scale(0) translateY(0); opacity: 1; }
              100% { transform: scale(1.5) translateY(-100px); opacity: 0; }
            }
            .animate-bounce-in {
              animation: bounceIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
            }
            @keyframes bounceIn {
              0% { transform: scale(0.3); opacity: 0; }
              100% { transform: scale(1); opacity: 1; }
            }
          `}</style>
        </div>
      )}

      {/* Contribution History Dialog */}
      <Dialog open={!!historyGoal} onOpenChange={(v) => { if (!v) { setHistoryGoal(null); setContributions([]); } }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <History className="w-5 h-5 text-cyan-400" /> {historyGoal?.name} - History
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Contribution history and milestones
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="contribution-history">
            {contributions.length === 0 ? (
              <p className="text-zinc-500 text-sm text-center py-4">No contributions yet</p>
            ) : (
              contributions.map((c, i) => (
                <div key={c.id || i} className="flex items-center justify-between py-2 border-b border-white/5">
                  <div>
                    <span className="text-sm text-emerald-400 font-mono">+{formatCurrency(c.amount)}</span>
                    {c.note && <p className="text-xs text-zinc-500 mt-0.5">{c.note}</p>}
                    {c.milestones_hit && c.milestones_hit.length > 0 && (
                      <div className="flex gap-1 mt-1">
                        {c.milestones_hit.map(m => (
                          <span key={m} className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded-sm font-mono">
                            {m}% milestone
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <span className="text-xs text-zinc-600">{new Date(c.date).toLocaleDateString()}</span>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
